import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Close any open details when clicking outside
    document.addEventListener('click', this.#handleClickOutside, {
      signal: this.#abortController.signal,
    });

    // Handle closing animation when summary is clicked
    this.querySelectorAll('details.drawer-submenu summary').forEach((summary) => {
      summary.addEventListener('click', (e) => {
        const details = summary.closest('details.drawer-submenu');
        if (!(details instanceof HTMLDetailsElement)) return;
        
        // If currently open, intercept the close and animate it
        if (
          details.hasAttribute('open') &&
          !summary.classList.contains('accordion-false') &&
          !(e.target instanceof Element && e.target.closest('a'))
        ) {
          e.preventDefault();
          this.#closeDetailsWithAnimation(details);
        } else {
          // If opening this menu, close all other open menus first
          const otherOpenDetails = this.querySelectorAll('details.drawer-submenu[open]');
          otherOpenDetails.forEach((otherDetails) => {
            if (otherDetails !== details && otherDetails instanceof HTMLDetailsElement) {
              this.#closeDetailsWithAnimation(otherDetails);
            }
          });
        }
      }, {
        signal: this.#abortController.signal,
      });
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // For <details> elements, open them temporarily to measure height
    const details = item?.closest('details');
    const wasOpen = details?.hasAttribute('open');
    if (details && !wasOpen) {
      details.setAttribute('open', '');
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    // Close details if it wasn't open before
    if (details && !wasOpen) {
      details.removeAttribute('open');
    }

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Handle clicks outside the header to close open menus
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Check if click is outside the header
    const header = target.closest('header, header-menu');
    if (!header) {
      // Close all open details in this menu with animation
      const openDetails = this.querySelectorAll('details.drawer-submenu[open]');
      openDetails.forEach((details) => {
        if (details instanceof HTMLDetailsElement) {
          this.#closeDetailsWithAnimation(details);
        }
      });
    }
  };

  /**
   * Close a details element with closing animation
   * @param {HTMLDetailsElement} details
   */
  #closeDetailsWithAnimation(details) {
    // Add closing class for animation
    details.classList.add('closing');
    
    // Wait for animation to complete before removing open attribute
    const animationDuration = parseInt(getComputedStyle(this).getPropertyValue('--submenu-animation-speed')) || 200;
    
    setTimeout(() => {
      details.removeAttribute('open');
      details.classList.remove('closing');
    }, animationDuration);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  if (!submenu) return null;
  
  // Find the inner content element to measure actual height
  const submenuInner = submenu.querySelector('.menu-list__submenu');
  if (submenuInner instanceof HTMLElement) return submenuInner;
  if (submenu instanceof HTMLElement) return submenu;
  return null;
}
